// C.1 — Действия Глобала. Резолверы по cardId.
//
// Покрытие (16 из 20 в xlsx v6 для C.1; легендарные карты с композитной
// логикой Подавления — следующая сессия):
//   common: Бронепакет, Перегруппировка, Винтовка GS-7
//   rare:   Бронежилет, Огневой вызов, Прокачка снаряжения, Вербовка,
//           Заброс, Залп подавления, Передислокация, Полевой госпиталь,
//           Заслон, Боевая выучка
//   epic:   Эвакуация, Развёртывание сектор-А, Тотальная глушилка
//   leg:    Поддержка дронов
//
// Стабы (возвращают «not_implemented_c1»):
//   - Боевой призыв (legendary, composite call)
//   - Финальный аккорд (legendary, damage по suppressed/silenced)
//   - Переговоры (legendary, special phase effect)

import { applyDamage } from '../../combat.js';
import { healUnit } from '../livlife/heal.js';
import { applyPermanentBuff, applyTemporaryBuff } from '../../buffs.js';
import { applySilence, applySilenceWithTTL } from '../../keywords/silence.js';
import { addPermanentKeyword } from '../../keywords/permanent.js';
import { KW } from '../../keywords.js';
import { moveUnit } from '../../move-unit.js';
import { evacuateUnitToHand } from '../livlife/evacuate.js';
import { summonUnit } from './summon.js';
import { resolveTarget } from '../../effects/targets.js';
import { registerImplementedAbility } from '../warn-unimplemented.js';

// Этап 25: регистрация всех 20 Действий Глобала в реестре реализованных.
const GLOBAL_ACTIONS_C1 = [
  'global_act_boevaya_vyuchka', 'global_act_boevoy_prizyv',
  'global_act_bronepacket', 'global_act_bronezhilet',
  'global_act_evakuatsiya', 'global_act_finalnyy_akkord',
  'global_act_ognevoy_vyzov', 'global_act_peredislokatsiya',
  'global_act_peregovory', 'global_act_peregruppirovka',
  'global_act_podderzhka_dronov', 'global_act_polevoy_gospital',
  'global_act_prokachka_snaryazheniya', 'global_act_razvyortyvanie_sektor_a',
  'global_act_totalnaya_glushilka', 'global_act_verbovka',
  'global_act_vintovka', 'global_act_zabros',
  'global_act_zalp_podavleniya', 'global_act_zaslon'
];
for (const id of GLOBAL_ACTIONS_C1) registerImplementedAbility(id, 'order');

const GSEC_FIGHTER_ID = 'global_gsec_fighter';

// Какие Действия требуют выбора цели игроком (для UI).
const NEEDS_TARGET = new Set([
  'global_act_bronepacket',
  'global_act_vintovka',
  'global_act_bronezhilet',
  'global_act_ognevoy_vyzov',
  'global_act_peregruppirovka',
  'global_act_peredislokatsiya',
  'global_act_evakuatsiya',
  'global_act_zaslon'
]);

export function actionNeedsTarget(cardId) {
  return NEEDS_TARGET.has(cardId);
}

export function getActionTargets(state, playerIdx, cardId) {
  const p = state.players?.[playerIdx];
  if (!p?.field) return [];
  const enemy = state.players?.[1 - playerIdx];

  const collectAllies = () => {
    const r = [];
    for (const row of p.field) {
      for (const u of row || []) if (u) r.push(u);
    }
    return r;
  };
  const collectEnemyUnits = () => {
    const r = [];
    if (!enemy?.field) return r;
    for (const row of enemy.field) {
      for (const u of row || []) if (u) r.push(u);
    }
    return r;
  };

  switch (cardId) {
    case 'global_act_bronepacket':
    case 'global_act_vintovka':
    case 'global_act_bronezhilet':
    case 'global_act_peregruppirovka':
    case 'global_act_peredislokatsiya':
    case 'global_act_evakuatsiya':
    case 'global_act_zaslon':
      return collectAllies();
    case 'global_act_ognevoy_vyzov':
      return collectEnemyUnits();
    default:
      return [];
  }
}

function freeSlotsOnLine(player, line) {
  const result = [];
  const row = player.field?.[line] || [];
  for (let s = 0; s < row.length; s++) {
    if (row[s] === null) result.push({ line, slot: s });
  }
  return result;
}

function findAnyFreeSlot(player) {
  for (let line = 0; line < 2; line++) {
    const free = freeSlotsOnLine(player, line);
    if (free.length > 0) return free[0];
  }
  return null;
}

// === Резолвер Действий ===
export function resolveAction(state, playerIdx, cardId, target, opts = {}) {
  const p = state.players?.[playerIdx];
  if (!p) return { ok: false, reason: 'no_player' };

  switch (cardId) {
    // ------- Common: buff_perm на одного союзника -------
    case 'global_act_bronepacket': {
      if (!target) return { ok: false, reason: 'no_target' };
      applyPermanentBuff(state, target, { atk: 0, armor: 1, hp: 2 }, cardId, 'Бронепакет Глобал');
      return { ok: true };
    }
    case 'global_act_vintovka': {
      if (!target) return { ok: false, reason: 'no_target' };
      applyPermanentBuff(state, target, { atk: 2, armor: 0, hp: 0 }, cardId, 'Винтовка GS-7');
      return { ok: true };
    }
    case 'global_act_bronezhilet': {
      if (!target) return { ok: false, reason: 'no_target' };
      applyPermanentBuff(state, target, { atk: 0, armor: 2, hp: 2 }, cardId, 'Бронежилет');
      return { ok: true };
    }

    // ------- Move (с picker) -------
    case 'global_act_peregruppirovka':
    case 'global_act_peredislokatsiya': {
      if (!target) return { ok: false, reason: 'no_target' };
      const destLine = opts?.destLine;
      const destSlot = opts?.destSlot;
      if (destLine == null || destSlot == null) return { ok: false, reason: 'no_destination' };
      return moveUnit(state, target, destLine, destSlot);
    }

    // ------- Damage -------
    case 'global_act_ognevoy_vyzov': {
      if (!target) return { ok: false, reason: 'no_target' };
      applyDamage(target, 3, { state, source: { id: cardId, name_ru: 'Огневой вызов' } });
      return { ok: true };
    }

    // ------- Buff hand+deck -------
    case 'global_act_prokachka_snaryazheniya': {
      const buffStats = { atk: 1, armor: 1, hp: 0 };
      let buffed = 0;
      for (const card of (p.hand || [])) {
        if (card?.type === 'unit') {
          applyPermanentBuff(state, card, buffStats, cardId, 'Прокачка снаряжения');
          buffed++;
        }
      }
      for (const card of (p.deck || [])) {
        if (card?.type === 'unit') {
          applyPermanentBuff(state, card, buffStats, cardId, 'Прокачка снаряжения');
          buffed++;
        }
      }
      state.log?.add(`🔧 Прокачка снаряжения: +1/+1/0 на ${buffed} юнитов в руке/колоде`);
      return { ok: true, buffed };
    }

    // ------- Summon G-Sec -------
    case 'global_act_verbovka': {
      const free = freeSlotsOnLine(p, 0);
      let summoned = 0;
      for (let i = 0; i < 2 && summoned < free.length; i++) {
        const s = free[summoned];
        if (summonUnit(state, playerIdx, GSEC_FIGHTER_ID, s.line, s.slot)) summoned++;
      }
      state.log?.add(`🪖 Вербовка: призван ${summoned} G-Sec`);
      return { ok: true, summoned };
    }
    case 'global_act_zabros': {
      // 3 Бойца в свободные ячейки, сначала 1-я линия.
      let summoned = 0;
      for (let line = 0; line < 2 && summoned < 3; line++) {
        const free = freeSlotsOnLine(p, line);
        for (const s of free) {
          if (summoned >= 3) break;
          if (summonUnit(state, playerIdx, GSEC_FIGHTER_ID, s.line, s.slot)) summoned++;
        }
      }
      state.log?.add(`🪖 Заброс: призван ${summoned} G-Sec`);
      return { ok: true, summoned };
    }

    // ------- Эвакуация / Заслон (return_to_hand) -------
    case 'global_act_evakuatsiya':
    case 'global_act_zaslon': {
      if (!target) return { ok: false, reason: 'no_target' };
      return evacuateUnitToHand(state, playerIdx, target,
        cardId === 'global_act_evakuatsiya' ? 'Эвакуация' : 'Заслон');
    }

    // ------- Полевой госпиталь (AoE heal + 1 G-Sec) -------
    case 'global_act_polevoy_gospital': {
      const allies = resolveTarget(state, p.hero, 'ally_aoe_all');
      for (const t of allies) healUnit(state, t, 2, { id: cardId, name_ru: 'Полевой госпиталь' });
      const slot = findAnyFreeSlot(p);
      if (slot) summonUnit(state, playerIdx, GSEC_FIGHTER_ID, slot.line, slot.slot);
      return { ok: true };
    }

    // ------- Поддержка дронов (heal_to_full all) -------
    case 'global_act_podderzhka_dronov': {
      const allies = resolveTarget(state, p.hero, 'ally_aoe_all');
      for (const t of allies) {
        const cur = t.current?.hp ?? 0;
        const max = t.base?.hp ?? cur;
        if (max > cur) healUnit(state, t, max - cur, { id: cardId, name_ru: 'Поддержка дронов' });
        if (t.current) t.current.armor = (typeof t.maxArmorWithMods === 'number') ? t.maxArmorWithMods : (t.base?.armor || 0);
      }
      return { ok: true };
    }

    // ------- Тотальная глушилка (silence enemy line 2 на 2 хода) -------
    case 'global_act_totalnaya_glushilka': {
      const enemy = state.players?.[1 - playerIdx];
      if (!enemy?.field) return { ok: false };
      const targets = enemy.field[1] || [];
      let silenced = 0;
      for (let i = 0; i < targets.length; i++) {
        const u = targets[i];
        if (!u) continue;
        // Этап 27: TTL Молчания через applySilenceWithTTL (2 хода).
        applySilenceWithTTL(state, u, `${cardId}:${u.instanceId || i}`, 2);
        silenced++;
      }
      state.log?.add(`📡 Тотальная глушилка: Молчание на ${silenced} вражеских юнитов 2-й линии (2 хода)`);
      return { ok: true, silenced };
    }

    // ------- Развёртывание сектор-А (composite) -------
    case 'global_act_razvyortyvanie_sektor_a': {
      const free = freeSlotsOnLine(p, 0);
      let summoned = 0;
      for (const s of free) {
        const u = summonUnit(state, playerIdx, GSEC_FIGHTER_ID, s.line, s.slot);
        if (u) {
          summoned++;
          // Случайно: Винтовка (+2/0/0) или Бронепакет (+0/1/2).
          const r = state.rng?.int ? state.rng.int(2) : Math.floor(Math.random() * 2);
          if (r === 0) {
            applyPermanentBuff(state, u, { atk: 2, armor: 0, hp: 0 }, 'global_act_vintovka', 'Винтовка GS-7');
          } else {
            applyPermanentBuff(state, u, { atk: 0, armor: 1, hp: 2 }, 'global_act_bronepacket', 'Бронепакет Глобал');
          }
        }
      }
      state.log?.add(`🎯 Развёртывание сектор-А: ${summoned} G-Sec с случайным экипом`);
      return { ok: true, summoned };
    }

    // ------- Залп подавления -------
    case 'global_act_zalp_podavleniya': {
      // Игрок выбирает линию (opts.chosenLine); пока default 0.
      const enemy = state.players?.[1 - playerIdx];
      if (!enemy?.field) return { ok: false };
      const line = opts?.chosenLine ?? 0;
      const targets = enemy.field[line] || [];
      let hits = 0;
      for (const u of targets) {
        if (!u) continue;
        applyDamage(u, 1, { state, source: { id: cardId, name_ru: 'Залп подавления' } });
        hits++;
        // Подавление-эффект на ячейке НА 2 ХОДА (фикс под текст: было вечное).
        // line=0 → -1 атаки (временный дебаф); line=1 → Молчание на 2 хода (TTL).
        if (line === 0) {
          applyTemporaryBuff(state, u, { atk: -1, armor: 0, hp: 0 }, (state.currentTurn ?? 1) + 2, cardId, 'Залп подавления');
        } else {
          applySilenceWithTTL(state, u, cardId, 2);
        }
      }
      return { ok: true, hits };
    }

    // ------- Боевая выучка (3 случайным союзникам по 1 случайному keyword) -------
    case 'global_act_boevaya_vyuchka': {
      const allies = resolveTarget(state, p.hero, 'ally_aoe_all').filter(u => u.type === 'unit');
      if (allies.length === 0) return { ok: true, applied: 0 };
      const KEYWORDS = [KW.TAUNT, KW.STEALTH, KW.PIERCE, KW.FRENZY];
      const N = Math.min(3, allies.length);
      const picked = new Set();
      let applied = 0;
      while (picked.size < N) {
        const idx = state.rng?.int ? state.rng.int(allies.length) : Math.floor(Math.random() * allies.length);
        picked.add(idx);
        if (picked.size > allies.length) break;
      }
      for (const idx of picked) {
        const u = allies[idx];
        const kw = KEYWORDS[state.rng?.int ? state.rng.int(KEYWORDS.length) : Math.floor(Math.random() * KEYWORDS.length)];
        addPermanentKeyword(state, u, kw, cardId, 'Боевая выучка');
        applied++;
      }
      return { ok: true, applied };
    }

    // ------- Боевой призыв (legendary, fill all empty + +2/0/0 каждому) -------
    case 'global_act_boevoy_prizyv': {
      let summoned = 0;
      for (let line = 0; line < 2; line++) {
        const free = freeSlotsOnLine(p, line);
        for (const slot of free) {
          const u = summonUnit(state, playerIdx, GSEC_FIGHTER_ID, slot.line, slot.slot);
          if (u) {
            applyPermanentBuff(state, u, { atk: 2, armor: 0, hp: 0 }, cardId, 'Боевой призыв');
            summoned++;
          }
        }
      }
      state.log?.add(`🪖 Боевой призыв: ${summoned} G-Sec заполнили поле, +2/0/0 каждому`);
      return { ok: true, summoned };
    }

    // ------- Финальный аккорд (legendary, 4 dmg suppressed/silenced) -------
    case 'global_act_finalnyy_akkord': {
      const enemy = state.players?.[1 - playerIdx];
      if (!enemy?.field) return { ok: true, hit: 0 };
      const targets = [];
      for (const row of enemy.field) for (const u of row || []) {
        if (!u || !u.isAlive || !u.isAlive()) continue;
        const isSilenced = !!u.current?.silenced;
        let isSuppressed = false;
        if (Array.isArray(u.permanentBuffs)) {
          for (const b of u.permanentBuffs) {
            if (typeof b.id === 'string' && b.id.startsWith('suppression:')) {
              isSuppressed = true; break;
            }
          }
        }
        if (isSilenced || isSuppressed) targets.push(u);
      }
      for (const t of targets) {
        applyDamage(t, 4, { state, source: { id: cardId, name_ru: 'Финальный аккорд' } });
      }
      state.log?.add(`🎼 Финальный аккорд: 4 урона ${targets.length} ослабл. вражеским юнитам`);
      return { ok: true, hit: targets.length };
    }

    // ------- Переговоры (legendary, freeze all attacks 1 turn + heal 2 all allies) -------
    case 'global_act_peregovory': {
      // Замораживаем все авто-атаки на 1 ход через state.swarmFrozenUntilTurn
      // (механизм используемый Стратегом Ли Пулом).
      const freezeTurn = (state.currentTurn ?? 1) + 1;
      state.swarmFrozenUntilTurn = Math.max(state.swarmFrozenUntilTurn || 0, freezeTurn);
      // Все союзные юниты лечатся на 2 hp.
      const allies = [];
      if (p.field) for (const row of p.field) for (const u of row || []) {
        if (u && u.isAlive && u.isAlive()) allies.push(u);
      }
      for (const t of allies) healUnit(state, t, 2, { id: cardId, name_ru: 'Переговоры' });
      state.log?.add(`🤝 Переговоры: бой остановлен 1 ход, +2 hp всем союзникам`);
      return { ok: true };
    }

    default:
      console.warn(`[Корпы] Неизвестное Действие Глобала: ${cardId}`);
      return { ok: false, reason: 'unknown_action' };
  }
}
